import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Modal — the shared dialog shell used across the dashboard.
 *
 * Standard usage (title + optional footer): the header (title + close) and
 * footer are rendered for you.
 *   <Modal isOpen onClose title="Add event" footer={<Buttons/>}>{body}</Modal>
 *
 * Bespoke dialogs that need their own header/layout: pass `hideHeader` and
 * `bodyClassName` and render everything (their own header/close + scroll) as
 * children — you still get the shared overlay, blur, centering, panel and
 * height cap.
 *   <Modal isOpen onClose hideHeader bodyClassName="flex flex-col min-h-0" maxWidth="max-w-4xl">{custom}</Modal>
 *
 * Props:
 *   isOpen, onClose, title, children, footer, maxWidth (default 'max-w-md'),
 *   hideHeader (default false), bodyClassName (overrides default body padding/scroll),
 *   closeOnBackdrop (default false — click on the dim backdrop closes).
 */
const Modal = ({
    isOpen,
    onClose,
    title,
    children,
    footer,
    maxWidth = 'max-w-md',
    hideHeader = false,
    bodyClassName,
    closeOnBackdrop = false,
}) => {
    if (!isOpen) return null;

    const onOverlayClick = closeOnBackdrop
        ? (e) => { if (e.target === e.currentTarget && onClose) onClose(); }
        : undefined;

    // Rendered through a portal to <body> so the overlay escapes the dashboard
    // layout's stacking contexts (sticky Navbar w/ backdrop-blur, Sidebar) and
    // reliably blurs/covers the whole viewport — including the header.
    return createPortal(
        // The overlay itself is the scroll container: a tall modal scrolls the
        // whole panel like a page (no cramped inner scrollbox), and is centered
        // when it fits. The header/footer stay sticky so close/actions are
        // always reachable.
        <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm overflow-y-auto scrollbar-hide">
            <div
                className="flex min-h-full items-center justify-center p-4"
                onClick={onOverlayClick}
            >
                <div className={`relative bg-white dark:bg-[#262b33] rounded-xl shadow-2xl w-full ${maxWidth} my-4 transform transition-all`}>

                    {/* Header (optional) */}
                    {!hideHeader && (
                        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 dark:border-gray-800 rounded-t-xl">
                            <h2 className="text-[16px] font-semibold text-[#334155] dark:text-gray-100 tracking-tight">{title}</h2>
                            <button
                                onClick={onClose}
                                className="flex items-center justify-center w-[22px] h-[22px] rounded-full bg-[#2563EB] text-white hover:bg-[#1d4ed8] transition-colors shadow-sm"
                            >
                                <X className="w-3.5 h-3.5" strokeWidth={3} />
                            </button>
                        </div>
                    )}

                    {/* Body */}
                    <div className={bodyClassName !== undefined ? bodyClassName : 'px-6 py-5'}>
                        {children}
                    </div>

                    {/* Footer (optional) */}
                    {footer && (
                        <div className="px-6 pb-6 pt-2">
                            {footer}
                        </div>
                    )}

                </div>
            </div>
        </div>,
        document.body
    );
};

export default Modal;
